import { useEffect, useState, Fragment } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import ExportButton from "../../components/ExportButton";
import { exportRowsToXlsx } from "../../lib/exportToXlsx";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Plus, FileDown, Bell, Trash2, IndianRupee, Eye, Pencil, ChevronDown, ChevronRight } from "lucide-react";

const todayIso = () => new Date().toISOString().slice(0, 10);
const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Billing() {
  const { user } = useAuth();
  const canCreate = ["partner", "admin"].includes(user?.role);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [partners, setPartners] = useState([]);
  const [ageing, setAgeing] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [openInv, setOpenInv] = useState(false);
  const [openPay, setOpenPay] = useState(null);
  const [invForm, setInvForm] = useState({
    client_id: "", project_id: "", invoice_number: "", invoice_type: "intra",
    invoice_date: todayIso(), due_date: todayIso(),
    amount: 0, out_of_pocket: 0, description: "", partner_id: "", place_of_supply: "Tamil Nadu", sac_code: "9982",
  });
  const [payForm, setPayForm] = useState({ payment_date: todayIso(), amount: 0, mode: "Bank Transfer", reference: "", notes: "", tds_amount: 0, tds_section: "194J", tds_certificate_no: "" });
  const [filterClient, setFilterClient] = useState("");
  const [invStartDate, setInvStartDate] = useState("");
  const [invEndDate, setInvEndDate] = useState("");
  const [periodMode, setPeriodMode] = useState("year");
  const [fy, setFy] = useState(() => {
    const t = new Date();
    const sy = t.getMonth() + 1 >= 4 ? t.getFullYear() : t.getFullYear() - 1;
    return `${sy}-${String(sy + 1).slice(2)}`;
  });
  const [tdsData, setTdsData] = useState(null);
  const [tdsFilter, setTdsFilter] = useState({ start: "", end: "", client_id: "", fy: "" });
  const [editingInv, setEditingInv] = useState(null);
  const [payments, setPayments] = useState([]);
  const [editPay, setEditPay] = useState(null);
  const [payEditForm, setPayEditForm] = useState({});
  const [expandedClient, setExpandedClient] = useState(new Set());
  const [expandedInv, setExpandedInv] = useState(new Set());
  const [invPayments, setInvPayments] = useState({});
  const [activeTab, setActiveTab] = useState("invoices");

  const toggleClientExp = (name) => {
    const next = new Set(expandedClient);
    if (next.has(name)) next.delete(name); else next.add(name);
    setExpandedClient(next);
  };
  const toggleInvExp = async (invId) => {
    const next = new Set(expandedInv);
    if (next.has(invId)) next.delete(invId);
    else {
      next.add(invId);
      if (!invPayments[invId]) {
        try { const { data } = await api.get(`/invoices/${invId}`); setInvPayments((s) => ({ ...s, [invId]: data.payments || [] })); }
        catch { setInvPayments((s) => ({ ...s, [invId]: [] })); }
      }
    }
    setExpandedInv(next);
  };

  const loadPayments = async () => {
    // Aggregate payments across all invoices by hitting /invoices/{id} — but simpler: expose from invoice detail on click.
    // Server has no /payments list endpoint, so use ageing feed or aggregate from invoices with paid_amount>0.
    try {
      const results = await Promise.all(invoices.filter(i => (i.paid_amount || 0) > 0 || (i.tds_amount || 0) > 0).map(async i => {
        try { const { data } = await api.get(`/invoices/${i.id}`); return (data.payments || []).map(p => ({ ...p, invoice_number: i.invoice_number, client_name: i.client_name })); } catch { return []; }
      }));
      setPayments(results.flat().sort((a,b) => (b.payment_date||"").localeCompare(a.payment_date||"")));
    } catch { setPayments([]); }
  };

  const openEditInv = (inv) => {
    setEditingInv(inv);
    setInvForm({
      client_id: inv.client_id || "", project_id: inv.project_id || "",
      invoice_number: inv.invoice_number || "", invoice_type: inv.invoice_type || "intra",
      invoice_date: inv.invoice_date || todayIso(), due_date: inv.due_date || todayIso(),
      amount: inv.amount || 0, out_of_pocket: inv.out_of_pocket || 0,
      description: inv.description || "", partner_id: inv.partner_id || "",
      place_of_supply: inv.place_of_supply || "Tamil Nadu", sac_code: inv.sac_code || "9982",
    });
    setOpenInv(true);
  };
  const savePayEdit = async () => {
    try {
      await api.patch(`/payments/${editPay.id}`, {
        amount: Number(payEditForm.amount || 0),
        tds_amount: Number(payEditForm.tds_amount || 0),
        payment_date: payEditForm.payment_date,
        mode: payEditForm.mode,
        reference: payEditForm.reference,
        notes: payEditForm.notes,
      });
      toast.success("Payment updated");
      setEditPay(null);
      await load();
      await loadPayments();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delPay = async (p) => {
    if (!window.confirm(`Delete payment of ${fmt(p.amount)} for ${p.invoice_number}?`)) return;
    try {
      await api.delete(`/payments/${p.id}`);
      toast.success("Payment deleted");
      await load();
      await loadPayments();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveRevenueOverride = async (month, value) => {
    try {
      await api.post("/billing/monthly-revenue-override", { month, revenue: Number(value), note: "" });
      toast.success(`Revenue for ${month} updated`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const load = () =>
    Promise.all([
      api.get(`/invoices?${[filterClient ? `client_id=${filterClient}` : null, invStartDate ? `start_date=${invStartDate}` : null, invEndDate ? `end_date=${invEndDate}` : null].filter(Boolean).join("&")}`).then((r) => setInvoices(r.data)),
      api.get("/clients").then((r) => setClients(r.data)),
      api.get("/projects").then((r) => setProjects(r.data)),
      api.get("/users").then((r) => setPartners(r.data.filter((u) => u.role === "partner"))),
      api.get("/billing/ageing").then((r) => setAgeing(r.data)).catch(() => {}),
      api.get(`/billing/monthly-revenue?${periodMode === "fy" ? `fy=${fy}` : `year=${year}`}`).then((r) => setRevenue(r.data)).catch(() => {}),
      api.get(`/billing/tds-summary?${[tdsFilter.start ? `start_date=${tdsFilter.start}` : null, tdsFilter.end ? `end_date=${tdsFilter.end}` : null, tdsFilter.client_id ? `client_id=${tdsFilter.client_id}` : null, tdsFilter.fy ? `fy=${tdsFilter.fy}` : null].filter(Boolean).join("&")}`).then((r) => setTdsData(r.data)).catch(() => {}),
    ]);

  useEffect(() => { load(); }, [year, fy, periodMode, filterClient, invStartDate, invEndDate, tdsFilter.start, tdsFilter.end, tdsFilter.client_id, tdsFilter.fy]);

  const submitInv = async (e) => {
    e.preventDefault();
    if (!invForm.client_id) { toast.error("Client required"); return; }
    try {
      if (editingInv) {
        await api.patch(`/invoices/${editingInv.id}`, {
          ...invForm,
          amount: Number(invForm.amount), out_of_pocket: Number(invForm.out_of_pocket || 0),
          project_id: invForm.project_id || null, partner_id: invForm.partner_id || null,
        });
        toast.success("Invoice updated");
      } else {
        await api.post("/invoices", {
          ...invForm,
          amount: Number(invForm.amount), out_of_pocket: Number(invForm.out_of_pocket || 0),
          project_id: invForm.project_id || null, partner_id: invForm.partner_id || null,
          invoice_number: invForm.invoice_number || null,
        });
        toast.success("Invoice created");
      }
      setOpenInv(false);
      setEditingInv(null);
      setInvForm({ client_id: "", project_id: "", invoice_number: "", invoice_type: "intra",
        invoice_date: todayIso(), due_date: todayIso(),
        amount: 0, out_of_pocket: 0, description: "", partner_id: "", place_of_supply: "Tamil Nadu", sac_code: "9982" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const previewGST = () => {
    const base = Number(invForm.amount || 0) + Number(invForm.out_of_pocket || 0);
    if (invForm.invoice_type === "intra") return { cgst: base * 0.09, sgst: base * 0.09, igst: 0, total: base * 1.18 };
    if (invForm.invoice_type === "inter") return { cgst: 0, sgst: 0, igst: base * 0.18, total: base * 1.18 };
    return { cgst: 0, sgst: 0, igst: 0, total: base };
  };
  const gstPreview = previewGST();

  const submitPay = async (e) => {
    e.preventDefault();
    if (!openPay) return;
    try {
      await api.post("/payments", {
        invoice_id: openPay.id, ...payForm,
        amount: Number(payForm.amount || 0),
        tds_amount: Number(payForm.tds_amount || 0),
      });
      toast.success("Payment recorded");
      setOpenPay(null);
      setPayForm({ payment_date: todayIso(), amount: 0, mode: "Bank Transfer", reference: "", notes: "", tds_amount: 0, tds_section: "194J", tds_certificate_no: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadPdf = async (inv) => {
    try {
      const r = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `invoice_${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("PDF download failed"); }
  };

  const viewPdf = async (inv) => {
    try {
      const r = await api.get(`/invoices/${inv.id}/pdf?view=true`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error("Cannot open PDF"); }
  };

  const sendReminder = async (inv) => {
    try {
      const r = await api.post(`/invoices/${inv.id}/remind`);
      toast.success(`Reminder sent to ${r.data.sent_to}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const delInv = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}?`)) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const statusColor = (s) => ({
    open: "border-amber-200 text-amber-700 bg-amber-50",
    partial: "border-blue-200 text-blue-700 bg-blue-50",
    paid: "border-emerald-200 text-emerald-700 bg-emerald-50",
  }[s] || "border-slate-200");

  const totalGST = (i) => Number(i.cgst || 0) + Number(i.sgst || 0) + Number(i.igst || 0);

  // Tab-aware "export what's on screen" — XLSX
  const doExport = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    if (activeTab === "invoices" || activeTab === "client-drill") {
      const cols = [
        { key: "client_name", label: "Client" },
        { key: "invoice_number", label: "No." },
        { key: "invoice_date", label: "Date" },
        { key: "due_date", label: "Due" },
        { key: "amount", label: "Amount (₹)" },
        { key: "out_of_pocket", label: "OPE (₹)" },
        { key: "gst", label: "GST (₹)", format: (_v, r) => totalGST(r) },
        { key: "total_amount", label: "Total (₹)" },
        { key: "paid_amount", label: "Paid (₹)" },
        { key: "tds_amount", label: "TDS (₹)" },
        { key: "balance", label: "Balance (₹)" },
        { key: "status", label: "Status" },
      ];
      const sorted = activeTab === "client-drill"
        ? [...invoices].sort((a, b) => (a.client_name || "").localeCompare(b.client_name || ""))
        : invoices;
      exportRowsToXlsx({ filename: `invoices_${stamp}.xlsx`, sheetName: activeTab === "client-drill" ? "Invoices by Client" : "Invoices", columns: cols, rows: sorted });
    } else if (activeTab === "collections") {
      exportRowsToXlsx({
        filename: `collections_${stamp}.xlsx`, sheetName: "Collections",
        columns: [
          { key: "payment_date", label: "Date" },
          { key: "invoice_number", label: "Invoice" },
          { key: "client_name", label: "Client" },
          { key: "amount", label: "Amount (₹)" },
          { key: "tds_amount", label: "TDS (₹)" },
          { key: "mode", label: "Mode" },
          { key: "reference", label: "Reference" },
          { key: "notes", label: "Notes" },
        ], rows: payments,
      });
    } else if (activeTab === "by-client") {
      exportRowsToXlsx({
        filename: `ageing_by_client_${stamp}.xlsx`, sheetName: "By Client",
        columns: [
          { key: "client_code", label: "Code" },
          { key: "client_name", label: "Client" },
          { key: "b0_30", label: "0-30 (₹)", format: (_v, r) => r.buckets?.["0-30"] || 0 },
          { key: "b31_60", label: "31-60 (₹)", format: (_v, r) => r.buckets?.["31-60"] || 0 },
          { key: "b61_90", label: "61-90 (₹)", format: (_v, r) => r.buckets?.["61-90"] || 0 },
          { key: "b90p", label: "90+ (₹)", format: (_v, r) => r.buckets?.["90+"] || 0 },
          { key: "total_outstanding", label: "Total Outstanding (₹)" },
        ], rows: ageing?.by_client || [],
      });
    } else if (activeTab === "by-partner") {
      exportRowsToXlsx({
        filename: `ageing_by_partner_${stamp}.xlsx`, sheetName: "By Partner",
        columns: [
          { key: "partner_name", label: "Partner" },
          { key: "b0_30", label: "0-30 (₹)", format: (_v, r) => r.buckets?.["0-30"] || 0 },
          { key: "b31_60", label: "31-60 (₹)", format: (_v, r) => r.buckets?.["31-60"] || 0 },
          { key: "b61_90", label: "61-90 (₹)", format: (_v, r) => r.buckets?.["61-90"] || 0 },
          { key: "b90p", label: "90+ (₹)", format: (_v, r) => r.buckets?.["90+"] || 0 },
          { key: "total_outstanding", label: "Total Outstanding (₹)" },
        ], rows: ageing?.by_partner || [],
      });
    } else if (activeTab === "revenue") {
      const rows = [...(revenue?.rows || [])];
      if (revenue?.totals) rows.push({ month: "TOTAL", ...revenue.totals });
      exportRowsToXlsx({
        filename: `monthly_revenue_${stamp}.xlsx`, sheetName: "Monthly Revenue",
        columns: [
          { key: "month", label: "Month" },
          { key: "revenue", label: "Revenue (₹)" },
          { key: "cgst", label: "CGST (₹)" },
          { key: "sgst", label: "SGST (₹)" },
          { key: "igst", label: "IGST (₹)" },
          { key: "total_gst", label: "Total GST (₹)" },
          { key: "total_billed", label: "Billed (₹)" },
        ], rows,
      });
    } else if (activeTab === "tds") {
      exportRowsToXlsx({
        filename: `tds_summary_${stamp}.xlsx`, sheetName: "TDS Detail",
        columns: [
          { key: "payment_date", label: "Date" },
          { key: "invoice_number", label: "Invoice" },
          { key: "client_name", label: "Client" },
          { key: "client_pan", label: "PAN" },
          { key: "amount", label: "Received (₹)" },
          { key: "tds_amount", label: "TDS (₹)" },
          { key: "tds_section", label: "Section" },
          { key: "tds_certificate_no", label: "Certificate" },
        ], rows: tdsData?.rows || [],
      });
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="billing-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Billing</div>
          <h1 className="font-sans text-4xl mt-2">Invoices & receivables</h1>
        </div>
        {canCreate && (
          <Dialog open={openInv} onOpenChange={(o) => { setOpenInv(o); if (!o) setEditingInv(null); }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="new-invoice-btn"><Plus className="h-4 w-4 mr-2"/>New Invoice</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-sans text-2xl">{editingInv ? `Edit Invoice · ${editingInv.invoice_number}` : "Create Invoice"}</DialogTitle></DialogHeader>
              <form onSubmit={submitInv} className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label className="eyebrow">Client</Label>
                  <Select value={invForm.client_id} onValueChange={(v)=>setInvForm({...invForm, client_id: v})}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="inv-client"><SelectValue placeholder="Select client"/></SelectTrigger>
                    <SelectContent>{clients.map((c)=><SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label className="eyebrow">Project (optional)</Label>
                  <Select value={invForm.project_id || "_none"} onValueChange={(v)=>setInvForm({...invForm, project_id: v === "_none" ? "" : v})}>
                    <SelectTrigger className="mt-2 rounded-sm"><SelectValue placeholder="—"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— None —</SelectItem>
                      {projects.filter((p)=>!invForm.client_id || p.client_id === invForm.client_id).map((p)=><SelectItem key={p.id} value={p.id}>{p.project_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label className="eyebrow">Invoice Type</Label>
                  <Select value={invForm.invoice_type} onValueChange={(v)=>setInvForm({...invForm, invoice_type: v})}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="inv-type"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="intra">Intra-state (9% CGST + 9% SGST)</SelectItem>
                      <SelectItem value="inter">Inter-state (18% IGST)</SelectItem>
                      <SelectItem value="sez">SEZ Unit (Zero-rated)</SelectItem>
                      <SelectItem value="export">Export (Zero-rated)</SelectItem>
                      <SelectItem value="proforma">Proforma (No GST)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">Invoice No (editable)</Label><Input value={invForm.invoice_number} onChange={(e)=>setInvForm({...invForm, invoice_number: e.target.value})} placeholder="Auto: AJ/FY/NNN" className="mt-2 rounded-sm font-mono" data-testid="inv-number"/></div>
                <div><Label className="eyebrow">Place of Supply</Label><Input value={invForm.place_of_supply} onChange={(e)=>setInvForm({...invForm, place_of_supply: e.target.value})} className="mt-2 rounded-sm" data-testid="inv-pos"/></div>
                <div><Label className="eyebrow">Invoice Date</Label><Input type="date" required value={invForm.invoice_date} onChange={(e)=>setInvForm({...invForm, invoice_date: e.target.value})} className="mt-2 rounded-sm" data-testid="inv-date"/></div>
                <div><Label className="eyebrow">Due Date</Label><Input type="date" required value={invForm.due_date} onChange={(e)=>setInvForm({...invForm, due_date: e.target.value})} className="mt-2 rounded-sm" data-testid="inv-due"/></div>
                <div><Label className="eyebrow">Taxable Amount (₹)</Label><Input type="number" step="0.01" required value={invForm.amount} onChange={(e)=>setInvForm({...invForm, amount: e.target.value})} className="mt-2 rounded-sm" data-testid="inv-amount"/></div>
                <div><Label className="eyebrow">Out-of-Pocket (₹)</Label><Input type="number" step="0.01" value={invForm.out_of_pocket} onChange={(e)=>setInvForm({...invForm, out_of_pocket: e.target.value})} className="mt-2 rounded-sm" data-testid="inv-ope"/></div>
                <div><Label className="eyebrow">Responsible Partner</Label>
                  <Select value={invForm.partner_id || "_none"} onValueChange={(v)=>setInvForm({...invForm, partner_id: v === "_none" ? "" : v})}>
                    <SelectTrigger className="mt-2 rounded-sm"><SelectValue placeholder="—"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— None —</SelectItem>
                      {partners.map((p)=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">SAC Code</Label><Input value={invForm.sac_code} onChange={(e)=>setInvForm({...invForm, sac_code: e.target.value})} className="mt-2 rounded-sm font-mono"/></div>
                <div className="col-span-2 bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="gst-preview">
                  <div className="eyebrow text-xs">GST Preview (auto-computed)</div>
                  <div className="grid grid-cols-4 gap-2 text-xs mt-2">
                    <div>CGST: <span className="serif-num">₹{gstPreview.cgst.toFixed(2)}</span></div>
                    <div>SGST: <span className="serif-num">₹{gstPreview.sgst.toFixed(2)}</span></div>
                    <div>IGST: <span className="serif-num">₹{gstPreview.igst.toFixed(2)}</span></div>
                    <div>Total: <span className="serif-num font-semibold">₹{gstPreview.total.toFixed(2)}</span></div>
                  </div>
                </div>
                <div className="col-span-2"><Label className="eyebrow">Description</Label><Textarea rows={2} value={invForm.description} onChange={(e)=>setInvForm({...invForm, description: e.target.value})} className="mt-2 rounded-sm"/></div>
                <DialogFooter className="col-span-2">
                  <Button type="button" variant="outline" onClick={()=>setOpenInv(false)} className="rounded-sm">Cancel</Button>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="inv-save-btn">Create Invoice</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {ageing && (
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-px bg-slate-200 border border-slate-200" data-testid="ageing-buckets">
          <div className="bg-white p-5"><div className="eyebrow">Not Due</div><div className="serif-num text-2xl mt-2">{fmt(ageing.buckets.not_due)}</div></div>
          <div className="bg-white p-5"><div className="eyebrow">0 – 30 days</div><div className="serif-num text-2xl mt-2 text-amber-700">{fmt(ageing.buckets["0-30"])}</div></div>
          <div className="bg-white p-5"><div className="eyebrow">31 – 60</div><div className="serif-num text-2xl mt-2 text-orange-700">{fmt(ageing.buckets["31-60"])}</div></div>
          <div className="bg-white p-5"><div className="eyebrow">61 – 90</div><div className="serif-num text-2xl mt-2 text-red-700">{fmt(ageing.buckets["61-90"])}</div></div>
          <div className="bg-white p-5"><div className="eyebrow">90+</div><div className="serif-num text-2xl mt-2 text-red-800">{fmt(ageing.buckets["90+"])}</div></div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="client-drill" data-testid="tab-client-drill">Client Drill-down</TabsTrigger>
          <TabsTrigger value="collections" data-testid="tab-collections" onClick={loadPayments}>Collections</TabsTrigger>
          <TabsTrigger value="by-client" data-testid="tab-by-client">By Client</TabsTrigger>
          <TabsTrigger value="by-partner" data-testid="tab-by-partner">By Partner</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="tab-revenue">Monthly Revenue</TabsTrigger>
          <TabsTrigger value="tds" data-testid="tab-tds">TDS Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          <div className="flex justify-end mb-3 gap-3 flex-wrap items-center">
            <Input type="date" value={invStartDate} onChange={(e)=>setInvStartDate(e.target.value)} className="rounded-sm w-40" placeholder="From" data-testid="inv-start-date"/>
            <span className="text-xs text-slate-400">to</span>
            <Input type="date" value={invEndDate} onChange={(e)=>setInvEndDate(e.target.value)} className="rounded-sm w-40" placeholder="To" data-testid="inv-end-date"/>
            <Select value={filterClient || "_all"} onValueChange={(v)=>setFilterClient(v === "_all" ? "" : v)}>
              <SelectTrigger className="rounded-sm w-56" data-testid="inv-client-filter"><SelectValue placeholder="All clients"/></SelectTrigger>
              <SelectContent><SelectItem value="_all">All clients</SelectItem>{clients.map((c)=><SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" onClick={doExport} data-testid="inv-export"><FileDown className="h-4 w-4 mr-2"/>Export XLSX</Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                <tr>
                  <th className="text-left p-4">No.</th>
                  <th className="text-left p-4">Date</th>
                  <th className="text-left p-4">Client</th>
                  <th className="text-left p-4">Due</th>
                  <th className="text-right p-4">Amount</th>
                  <th className="text-right p-4">OPE</th>
                  <th className="text-right p-4">GST</th>
                  <th className="text-right p-4">Total</th>
                  <th className="text-right p-4">Paid</th>
                  <th className="text-right p-4">TDS</th>
                  <th className="text-right p-4">Balance</th>
                  <th className="text-left p-4">Status</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="invoices-table">
                {invoices.length === 0 && <tr><td colSpan={13} className="p-8 text-center text-slate-500">No invoices.</td></tr>}
                {invoices.map((i) => (
                  <tr key={i.id} className="hover:bg-slate-50">
                    <td className="p-4 font-mono text-xs">{i.invoice_number}</td>
                    <td className="p-4 font-mono text-xs">{i.invoice_date}</td>
                    <td className="p-4 font-medium">{i.client_name}</td>
                    <td className="p-4 font-mono text-xs">
                      {i.due_date}
                      {i.overdue && <div className="text-red-600 text-xs mt-1">↳ {i.overdue_days}d overdue</div>}
                    </td>
                    <td className="p-4 text-right serif-num">{fmt(i.amount)}</td>
                    <td className="p-4 text-right serif-num text-slate-500">{fmt(i.out_of_pocket || 0)}</td>
                    <td className="p-4 text-right serif-num text-slate-500">{fmt(totalGST(i))}</td>
                    <td className="p-4 text-right serif-num font-semibold">{fmt(i.total_amount)}</td>
                    <td className="p-4 text-right serif-num text-emerald-700">{fmt(i.paid_amount)}</td>
                    <td className="p-4 text-right serif-num text-blue-700">{fmt(i.tds_amount || 0)}</td>
                    <td className="p-4 text-right serif-num text-red-700">{fmt(i.balance)}</td>
                    <td className="p-4"><span className={`text-xs uppercase tracking-[0.2em] px-2 py-1 border rounded-full ${statusColor(i.status)}`}>{i.status}</span></td>
                    <td className="p-4 text-right whitespace-nowrap space-x-1">
                      {i.balance > 0 && ["partner","admin","accounts"].includes(user?.role) && <Button size="sm" onClick={()=>{ setOpenPay(i); setPayForm({...payForm, amount: i.balance}); }} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs" data-testid={`record-pay-${i.id}`}><IndianRupee className="h-3 w-3 mr-1"/>Pay</Button>}
                      {canCreate && <button onClick={()=>openEditInv(i)} className="text-slate-500 hover:text-blue-700 inline-block ml-1" title="Edit invoice" data-testid={`inv-edit-${i.id}`}><Pencil className="h-4 w-4"/></button>}
                      <button onClick={()=>viewPdf(i)} className="text-slate-500 hover:text-blue-700 inline-block mr-1" title="View PDF" data-testid={`inv-view-${i.id}`}><Eye className="h-4 w-4"/></button>
                      <button onClick={()=>downloadPdf(i)} className="text-slate-500 hover:text-slate-900 inline-block" title="Download PDF" data-testid={`inv-pdf-${i.id}`}><FileDown className="h-4 w-4"/></button>
                      {i.overdue && canCreate && <button onClick={()=>sendReminder(i)} className="text-amber-600 hover:text-amber-800 inline-block ml-1" title="Send reminder" data-testid={`inv-remind-${i.id}`}><Bell className="h-4 w-4"/></button>}
                      {canCreate && i.paid_amount === 0 && <button onClick={()=>delInv(i)} className="text-slate-400 hover:text-red-600 inline-block ml-1" title="Delete"><Trash2 className="h-4 w-4"/></button>}
                    </td>
                  </tr>
                ))}
                {invoices.length > 0 && (
                  <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300" data-testid="invoices-totals-row">
                    <td colSpan={4} className="p-4 text-right uppercase tracking-[0.18em] text-xs text-slate-600">Sub-total ({invoices.length} invoices)</td>
                    <td className="p-4 text-right serif-num">{fmt(invoices.reduce((s, i)=>s + (i.amount || 0), 0))}</td>
                    <td className="p-4 text-right serif-num text-slate-500">{fmt(invoices.reduce((s, i)=>s + (i.out_of_pocket || 0), 0))}</td>
                    <td className="p-4 text-right serif-num text-slate-500">{fmt(invoices.reduce((s, i)=>s + totalGST(i), 0))}</td>
                    <td className="p-4 text-right serif-num">{fmt(invoices.reduce((s, i)=>s + (i.total_amount || 0), 0))}</td>
                    <td className="p-4 text-right serif-num text-emerald-700">{fmt(invoices.reduce((s, i)=>s + (i.paid_amount || 0), 0))}</td>
                    <td className="p-4 text-right serif-num text-blue-700">{fmt(invoices.reduce((s, i)=>s + (i.tds_amount || 0), 0))}</td>
                    <td className="p-4 text-right serif-num text-red-700">{fmt(invoices.reduce((s, i)=>s + (i.balance || 0), 0))}</td>
                    <td colSpan={2}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="client-drill" className="mt-4">
          <div className="flex justify-end mb-2">
            <Button variant="outline" size="sm" onClick={doExport} data-testid="drill-export"><FileDown className="h-4 w-4 mr-2"/>Export XLSX</Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="client-drill-tab">
            <div className="p-3 eyebrow border-b border-slate-100">Group invoices by client · click a client to expand Month/Status/Invoices, click an invoice to see line-items &amp; payment history</div>
            <InvoicesByClient
              invoices={invoices}
              expandedClient={expandedClient}
              expandedInv={expandedInv}
              toggleClient={toggleClientExp}
              toggleInv={toggleInvExp}
              invPayments={invPayments}
              user={user}
              canCreate={canCreate}
              payForm={payForm}
              setOpenPay={setOpenPay}
              setPayForm={setPayForm}
              openEditInv={openEditInv}
              viewPdf={viewPdf}
              downloadPdf={downloadPdf}
              sendReminder={sendReminder}
              delInv={delInv}
              fmt={fmt}
              totalGST={totalGST}
              statusColor={statusColor}
            />
          </div>
        </TabsContent>

        <TabsContent value="collections" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="collections-list">
            <div className="p-4 flex items-center justify-between border-b border-slate-100">
              <div>
                <div className="eyebrow">Collections · payments received</div>
                <div className="text-xs text-slate-500 mt-1">Partner and HR/Admin can edit or delete any collection.</div>
              </div>
              <Button variant="outline" size="sm" onClick={loadPayments} data-testid="reload-payments">Reload</Button>
              <Button variant="outline" size="sm" onClick={doExport} data-testid="collections-export" className="ml-2"><FileDown className="h-4 w-4 mr-2"/>Export XLSX</Button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                <tr>
                  <th className="text-left p-4">Date</th>
                  <th className="text-left p-4">Invoice</th>
                  <th className="text-left p-4">Client</th>
                  <th className="text-right p-4">Amount</th>
                  <th className="text-right p-4">TDS</th>
                  <th className="text-left p-4">Mode</th>
                  <th className="text-left p-4">Ref</th>
                  <th className="text-left p-4">Notes</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payments.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-500">No collections yet. Click Reload once you have invoices with payments.</td></tr>}
                {payments.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50" data-testid={`pay-row-${p.id}`}>
                    <td className="p-4 font-mono text-xs">{p.payment_date}</td>
                    <td className="p-4 font-mono text-xs">{p.invoice_number}</td>
                    <td className="p-4">{p.client_name}</td>
                    <td className="p-4 text-right serif-num text-emerald-700">{fmt(p.amount)}</td>
                    <td className="p-4 text-right serif-num text-blue-700">{fmt(p.tds_amount || 0)}</td>
                    <td className="p-4 text-xs">{p.mode}</td>
                    <td className="p-4 text-xs font-mono">{p.reference}</td>
                    <td className="p-4 text-xs text-slate-500 max-w-xs truncate">{p.notes}</td>
                    <td className="p-4 text-right whitespace-nowrap">
                      {canCreate && <button onClick={()=>{ setEditPay(p); setPayEditForm({ amount: p.amount, tds_amount: p.tds_amount || 0, payment_date: p.payment_date, mode: p.mode, reference: p.reference || "", notes: p.notes || "" }); }} className="text-slate-500 hover:text-blue-700 mr-2" title="Edit" data-testid={`pay-edit-${p.id}`}><Pencil className="h-4 w-4"/></button>}
                      {canCreate && <button onClick={()=>delPay(p)} className="text-slate-400 hover:text-red-600" title="Delete" data-testid={`pay-del-${p.id}`}><Trash2 className="h-4 w-4"/></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editPay && (
            <Dialog open onOpenChange={(o) => !o && setEditPay(null)}>
              <DialogContent className="max-w-lg" data-testid="pay-edit-dialog">
                <DialogHeader><DialogTitle className="font-sans text-xl">Edit Collection · {editPay.invoice_number}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="eyebrow">Date</Label><Input type="date" value={payEditForm.payment_date} onChange={(e)=>setPayEditForm({...payEditForm, payment_date: e.target.value})} className="mt-2 rounded-sm"/></div>
                  <div><Label className="eyebrow">Mode</Label><Input value={payEditForm.mode} onChange={(e)=>setPayEditForm({...payEditForm, mode: e.target.value})} className="mt-2 rounded-sm"/></div>
                  <div><Label className="eyebrow">Amount (₹)</Label><Input type="number" step="0.01" value={payEditForm.amount} onChange={(e)=>setPayEditForm({...payEditForm, amount: e.target.value})} className="mt-2 rounded-sm" data-testid="pay-edit-amount"/></div>
                  <div><Label className="eyebrow">TDS (₹)</Label><Input type="number" step="0.01" value={payEditForm.tds_amount} onChange={(e)=>setPayEditForm({...payEditForm, tds_amount: e.target.value})} className="mt-2 rounded-sm"/></div>
                  <div className="col-span-2"><Label className="eyebrow">Reference</Label><Input value={payEditForm.reference} onChange={(e)=>setPayEditForm({...payEditForm, reference: e.target.value})} className="mt-2 rounded-sm"/></div>
                  <div className="col-span-2"><Label className="eyebrow">Notes</Label><Textarea value={payEditForm.notes} onChange={(e)=>setPayEditForm({...payEditForm, notes: e.target.value})} className="mt-2 rounded-sm"/></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditPay(null)}>Cancel</Button>
                  <Button onClick={savePayEdit} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="pay-edit-save">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </TabsContent>

        <TabsContent value="by-client" className="mt-4">
          <div className="flex justify-end mb-2">
            <Button variant="outline" size="sm" onClick={doExport} data-testid="by-client-export"><FileDown className="h-4 w-4 mr-2"/>Export XLSX</Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                <tr><th className="text-left p-4">Code</th><th className="text-left p-4">Client</th><th className="text-right p-4">0-30</th><th className="text-right p-4">31-60</th><th className="text-right p-4">61-90</th><th className="text-right p-4">90+</th><th className="text-right p-4 font-semibold">Total Outstanding</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="ageing-by-client">
                {(ageing?.by_client || []).length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No outstanding receivables.</td></tr>}
                {(ageing?.by_client || []).map((c) => (
                  <tr key={c.client_id} className="hover:bg-slate-50">
                    <td className="p-4 font-mono text-xs">{c.client_code}</td>
                    <td className="p-4 font-medium">{c.client_name}</td>
                    <td className="p-4 text-right serif-num">{fmt(c.buckets["0-30"])}</td>
                    <td className="p-4 text-right serif-num">{fmt(c.buckets["31-60"])}</td>
                    <td className="p-4 text-right serif-num">{fmt(c.buckets["61-90"])}</td>
                    <td className="p-4 text-right serif-num text-red-700">{fmt(c.buckets["90+"])}</td>
                    <td className="p-4 text-right serif-num font-semibold">{fmt(c.total_outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="by-partner" className="mt-4">
          <div className="flex justify-end mb-2">
            <Button variant="outline" size="sm" onClick={doExport} data-testid="by-partner-export"><FileDown className="h-4 w-4 mr-2"/>Export XLSX</Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                <tr><th className="text-left p-4">Partner</th><th className="text-right p-4">0-30</th><th className="text-right p-4">31-60</th><th className="text-right p-4">61-90</th><th className="text-right p-4">90+</th><th className="text-right p-4 font-semibold">Total Outstanding</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="ageing-by-partner">
                {(ageing?.by_partner || []).length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No partner-tagged invoices outstanding.</td></tr>}
                {(ageing?.by_partner || []).map((p) => (
                  <tr key={p.partner_id} className="hover:bg-slate-50">
                    <td className="p-4 font-medium">{p.partner_name}</td>
                    <td className="p-4 text-right serif-num">{fmt(p.buckets["0-30"])}</td>
                    <td className="p-4 text-right serif-num">{fmt(p.buckets["31-60"])}</td>
                    <td className="p-4 text-right serif-num">{fmt(p.buckets["61-90"])}</td>
                    <td className="p-4 text-right serif-num text-red-700">{fmt(p.buckets["90+"])}</td>
                    <td className="p-4 text-right serif-num font-semibold">{fmt(p.total_outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="revenue" className="mt-4">
          <div className="flex items-end gap-3 mb-3 flex-wrap">
            <Select value={periodMode} onValueChange={setPeriodMode}>
              <SelectTrigger className="rounded-sm w-44" data-testid="rev-period-mode"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="year">Calendar Year</SelectItem><SelectItem value="fy">Financial Year</SelectItem></SelectContent>
            </Select>
            {periodMode === "year" ? (
              <div><Label className="eyebrow">Year</Label><Input value={year} onChange={(e)=>setYear(e.target.value)} className="mt-2 rounded-sm w-28" data-testid="revenue-year"/></div>
            ) : (
              <div><Label className="eyebrow">FY (YYYY-YY)</Label><Input value={fy} onChange={(e)=>setFy(e.target.value)} placeholder="2025-26" className="mt-2 rounded-sm w-32 font-mono" data-testid="revenue-fy"/></div>
            )}
            <div className="text-xs text-slate-500">Tip: Click on a revenue cell to manually edit it.</div>
            <div className="flex-1"/>
            <Button variant="outline" size="sm" onClick={doExport} data-testid="revenue-export"><FileDown className="h-4 w-4 mr-2"/>Export XLSX</Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                <tr><th className="text-left p-4">Month</th><th className="text-right p-4">Revenue</th><th className="text-right p-4">CGST</th><th className="text-right p-4">SGST</th><th className="text-right p-4">IGST</th><th className="text-right p-4">Total GST</th><th className="text-right p-4 font-semibold">Billed</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="monthly-revenue-table">
                {(revenue?.rows || []).map((r) => (
                  <tr key={r.month} className="hover:bg-slate-50">
                    <td className="p-4 font-mono text-xs">{r.month}</td>
                    <td className="p-4 text-right serif-num">
                      <input
                        type="number"
                        step="0.01"
                        defaultValue={r.revenue}
                        onBlur={(e)=>{ const v = Number(e.target.value); if (v !== r.revenue) saveRevenueOverride(r.month, v); }}
                        className={`text-right w-32 bg-transparent border-b border-dashed border-slate-300 hover:border-slate-500 focus:border-slate-900 focus:outline-none ${r.is_override ? "text-blue-700" : ""}`}
                        data-testid={`rev-input-${r.month}`}
                      />
                      {r.is_override && <span className="ml-1 text-xs text-blue-600" title={`Auto: ₹${r.revenue_auto}`}>✎</span>}
                    </td>
                    <td className="p-4 text-right serif-num text-slate-500">{fmt(r.cgst)}</td>
                    <td className="p-4 text-right serif-num text-slate-500">{fmt(r.sgst)}</td>
                    <td className="p-4 text-right serif-num text-slate-500">{fmt(r.igst)}</td>
                    <td className="p-4 text-right serif-num">{fmt(r.total_gst)}</td>
                    <td className="p-4 text-right serif-num font-semibold">{fmt(r.total_billed)}</td>
                  </tr>
                ))}
                {revenue?.totals && (
                  <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300">
                    <td className="p-4">Total</td>
                    <td className="p-4 text-right serif-num">{fmt(revenue.totals.revenue)}</td>
                    <td className="p-4 text-right serif-num">{fmt(revenue.totals.cgst)}</td>
                    <td className="p-4 text-right serif-num">{fmt(revenue.totals.sgst)}</td>
                    <td className="p-4 text-right serif-num">{fmt(revenue.totals.igst)}</td>
                    <td className="p-4 text-right serif-num">{fmt(revenue.totals.total_gst)}</td>
                    <td className="p-4 text-right serif-num">{fmt(revenue.totals.total_billed)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="tds" className="mt-4" data-testid="tab-content-tds">
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div><Label className="eyebrow">From</Label><Input type="date" value={tdsFilter.start} onChange={(e)=>setTdsFilter({...tdsFilter, start: e.target.value, fy: ""})} className="rounded-sm mt-2 w-40" data-testid="tds-start"/></div>
            <div><Label className="eyebrow">To</Label><Input type="date" value={tdsFilter.end} onChange={(e)=>setTdsFilter({...tdsFilter, end: e.target.value, fy: ""})} className="rounded-sm mt-2 w-40" data-testid="tds-end"/></div>
            <div><Label className="eyebrow">Client</Label>
              <Select value={tdsFilter.client_id || "_all"} onValueChange={(v)=>setTdsFilter({...tdsFilter, client_id: v === "_all" ? "" : v})}>
                <SelectTrigger className="rounded-sm mt-2 w-56" data-testid="tds-client"><SelectValue placeholder="All clients"/></SelectTrigger>
                <SelectContent><SelectItem value="_all">All clients</SelectItem>{clients.map((c)=><SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={doExport} data-testid="tds-export"><FileDown className="h-4 w-4 mr-2"/>Export XLSX</Button>
          </div>
          {tdsData && (
            <>
              <div className="grid grid-cols-3 gap-px bg-slate-200 border border-slate-200 mb-4">
                <div className="bg-white p-5"><div className="eyebrow">Total TDS</div><div className="serif-num text-3xl mt-2 text-blue-700">{fmt(tdsData.totals.tds_amount)}</div></div>
                <div className="bg-white p-5"><div className="eyebrow">Amount Received</div><div className="serif-num text-3xl mt-2 text-emerald-700">{fmt(tdsData.totals.received_amount)}</div></div>
                <div className="bg-white p-5"><div className="eyebrow">Transactions</div><div className="serif-num text-3xl mt-2">{tdsData.totals.count}</div></div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                  <div className="p-3 border-b border-slate-200 eyebrow">By Section</div>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs"><tr><th className="text-left p-3">Section</th><th className="text-right p-3">Count</th><th className="text-right p-3">TDS</th></tr></thead>
                    <tbody className="divide-y divide-slate-100" data-testid="tds-by-section">
                      {tdsData.by_section.length === 0 && <tr><td colSpan={3} className="p-4 text-center text-slate-500 text-xs">No TDS records.</td></tr>}
                      {tdsData.by_section.map((s) => <tr key={s.section} className="hover:bg-slate-50"><td className="p-3 font-mono text-xs">{s.section}</td><td className="p-3 text-right serif-num">{s.count}</td><td className="p-3 text-right serif-num">{fmt(s.tds)}</td></tr>)}
                    </tbody>
                  </table>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                  <div className="p-3 border-b border-slate-200 eyebrow">By Client</div>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs"><tr><th className="text-left p-3">Client</th><th className="text-left p-3">PAN</th><th className="text-right p-3">TDS</th></tr></thead>
                    <tbody className="divide-y divide-slate-100" data-testid="tds-by-client">
                      {tdsData.by_client.length === 0 && <tr><td colSpan={3} className="p-4 text-center text-slate-500 text-xs">No TDS records.</td></tr>}
                      {tdsData.by_client.map((c, i) => <tr key={i} className="hover:bg-slate-50"><td className="p-3 font-medium">{c.client_name}</td><td className="p-3 font-mono text-xs">{c.client_pan}</td><td className="p-3 text-right serif-num">{fmt(c.tds)}</td></tr>)}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
                <div className="p-3 border-b border-slate-200 eyebrow">Detailed TDS Entries</div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                    <tr><th className="text-left p-3">Date</th><th className="text-left p-3">Invoice</th><th className="text-left p-3">Client</th><th className="text-left p-3">PAN</th><th className="text-right p-3">Received</th><th className="text-right p-3">TDS</th><th className="text-left p-3">Section</th><th className="text-left p-3">Certificate</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-testid="tds-detail-table">
                    {tdsData.rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-500">No TDS records for this period.</td></tr>}
                    {tdsData.rows.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="p-3 font-mono text-xs">{r.payment_date}</td>
                        <td className="p-3 font-mono text-xs">{r.invoice_number}</td>
                        <td className="p-3 font-medium">{r.client_name}</td>
                        <td className="p-3 font-mono text-xs">{r.client_pan}</td>
                        <td className="p-3 text-right serif-num">{fmt(r.amount)}</td>
                        <td className="p-3 text-right serif-num text-blue-700">{fmt(r.tds_amount)}</td>
                        <td className="p-3 font-mono text-xs">{r.tds_section || "—"}</td>
                        <td className="p-3 text-xs text-slate-600">{r.tds_certificate_no || "—"}</td>
                      </tr>
                    ))}
                    {tdsData.rows.length > 0 && (
                      <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300" data-testid="tds-detail-totals">
                        <td colSpan={4} className="p-3 text-right uppercase tracking-[0.18em] text-xs text-slate-600">Total</td>
                        <td className="p-3 text-right serif-num">{fmt(tdsData.totals.received_amount)}</td>
                        <td className="p-3 text-right serif-num text-blue-700">{fmt(tdsData.totals.tds_amount)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!openPay} onOpenChange={(o)=>!o && setOpenPay(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-sans text-xl">Record Payment</DialogTitle></DialogHeader>
          <form onSubmit={submitPay} className="space-y-4">
            <div className="text-sm text-slate-600">Invoice {openPay?.invoice_number} · Balance {fmt(openPay?.balance)}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="eyebrow">Date</Label><Input type="date" required value={payForm.payment_date} onChange={(e)=>setPayForm({...payForm, payment_date: e.target.value})} className="mt-2 rounded-sm" data-testid="pay-date"/></div>
              <div><Label className="eyebrow">Amount</Label><Input type="number" step="0.01" required value={payForm.amount} onChange={(e)=>setPayForm({...payForm, amount: e.target.value})} className="mt-2 rounded-sm" data-testid="pay-amount"/></div>
              <div className="col-span-2"><Label className="eyebrow">Mode</Label>
                <Select value={payForm.mode} onValueChange={(v)=>setPayForm({...payForm, mode: v})}>
                  <SelectTrigger className="mt-2 rounded-sm"><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="Bank Transfer">Bank Transfer</SelectItem><SelectItem value="UPI">UPI</SelectItem><SelectItem value="Cheque">Cheque</SelectItem><SelectItem value="Cash">Cash</SelectItem><SelectItem value="NEFT">NEFT</SelectItem><SelectItem value="RTGS">RTGS</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="eyebrow">Reference / UTR</Label><Input value={payForm.reference} onChange={(e)=>setPayForm({...payForm, reference: e.target.value})} className="mt-2 rounded-sm" data-testid="pay-reference"/></div>
              <div className="col-span-2 border-t border-slate-200 pt-3 mt-1 eyebrow text-xs">TDS Deducted (if any)</div>
              <div><Label className="eyebrow">TDS Amount</Label><Input type="number" step="0.01" value={payForm.tds_amount || 0} onChange={(e)=>setPayForm({...payForm, tds_amount: e.target.value})} className="mt-2 rounded-sm" data-testid="pay-tds-amount"/></div>
              <div><Label className="eyebrow">Section</Label>
                <Select value={payForm.tds_section || "194J"} onValueChange={(v)=>setPayForm({...payForm, tds_section: v})}>
                  <SelectTrigger className="mt-2 rounded-sm" data-testid="pay-tds-section"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="194J">194J — Professional fees (10%)</SelectItem>
                    <SelectItem value="194JA">194JA — Technical services (2%)</SelectItem>
                    <SelectItem value="194C">194C — Contractor (1/2%)</SelectItem>
                    <SelectItem value="194H">194H — Commission</SelectItem>
                    <SelectItem value="194I">194I — Rent</SelectItem>
                    <SelectItem value="194Q">194Q — Purchase of goods</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="eyebrow">TDS Certificate / Reference No.</Label><Input value={payForm.tds_certificate_no || ""} onChange={(e)=>setPayForm({...payForm, tds_certificate_no: e.target.value})} placeholder="e.g. Form 16A reference" className="mt-2 rounded-sm" data-testid="pay-tds-cert"/></div>
              <div className="col-span-2"><Label className="eyebrow">Notes</Label><Textarea rows={2} value={payForm.notes} onChange={(e)=>setPayForm({...payForm, notes: e.target.value})} className="mt-2 rounded-sm"/></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setOpenPay(null)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="pay-submit-btn">Record</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================================
// InvoicesByClient — grouped drill-down
// =========================================================
function InvoicesByClient({ invoices, expandedClient, expandedInv, toggleClient, toggleInv, invPayments,
                            user, canCreate, payForm, setOpenPay, setPayForm, openEditInv, viewPdf,
                            downloadPdf, sendReminder, delInv, fmt, totalGST, statusColor }) {
  if (!invoices.length) return <div className="p-8 text-center text-slate-500">No invoices.</div>;
  const groups = {};
  invoices.forEach((i) => {
    const key = i.client_name || "— Not linked —";
    if (!groups[key]) groups[key] = { name: key, invoices: [], billed: 0, paid: 0, tds: 0, balance: 0, ope: 0, overdue_count: 0 };
    const g = groups[key];
    g.invoices.push(i);
    g.billed += i.total_amount || 0;
    g.paid += i.paid_amount || 0;
    g.tds += i.tds_amount || 0;
    g.balance += i.balance || 0;
    g.ope += i.out_of_pocket || 0;
    if (i.overdue) g.overdue_count += 1;
  });
  const sorted = Object.values(groups).sort((a, b) => b.billed - a.billed);
  const totals = sorted.reduce((s, g) => ({
    inv: s.inv + g.invoices.length,
    billed: s.billed + g.billed,
    paid: s.paid + g.paid,
    tds: s.tds + g.tds,
    balance: s.balance + g.balance,
    overdue: s.overdue + g.overdue_count,
  }), { inv: 0, billed: 0, paid: 0, tds: 0, balance: 0, overdue: 0 });

  return (
    <table className="w-full text-sm" data-testid="invoices-table">
      <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
        <tr>
          <th className="p-4 w-8"></th>
          <th className="text-left p-4">Client</th>
          <th className="text-right p-4"># Invoices</th>
          <th className="text-right p-4">Billed</th>
          <th className="text-right p-4">Paid</th>
          <th className="text-right p-4">TDS</th>
          <th className="text-right p-4">Balance</th>
          <th className="text-right p-4">Overdue</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {sorted.map((g) => {
          const isOpen = expandedClient.has(g.name);
          return (
            <Fragment key={g.name}>
              <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => toggleClient(g.name)} data-testid={`inv-client-${g.name}`}>
                <td className="p-4">{isOpen ? <ChevronDown className="h-4 w-4 text-slate-500"/> : <ChevronRight className="h-4 w-4 text-slate-400"/>}</td>
                <td className="p-4 font-medium text-slate-800">{g.name}</td>
                <td className="p-4 text-right">{g.invoices.length}</td>
                <td className="p-4 text-right serif-num">{fmt(g.billed)}</td>
                <td className="p-4 text-right serif-num text-emerald-700">{fmt(g.paid)}</td>
                <td className="p-4 text-right serif-num text-blue-700">{fmt(g.tds)}</td>
                <td className="p-4 text-right serif-num text-red-700 font-semibold">{fmt(g.balance)}</td>
                <td className="p-4 text-right">{g.overdue_count > 0 ? <span className="text-xs px-2 py-0.5 bg-amber-100 border border-amber-200 text-amber-800 rounded-full">{g.overdue_count}</span> : <span className="text-slate-400">—</span>}</td>
              </tr>
              {isOpen && (
                <tr className="bg-slate-50">
                  <td colSpan={8} className="p-4">
                    <ClientInvoicesDrill
                      client={g}
                      expandedInv={expandedInv}
                      toggleInv={toggleInv}
                      invPayments={invPayments}
                      user={user}
                      canCreate={canCreate}
                      payForm={payForm}
                      setOpenPay={setOpenPay}
                      setPayForm={setPayForm}
                      openEditInv={openEditInv}
                      viewPdf={viewPdf}
                      downloadPdf={downloadPdf}
                      sendReminder={sendReminder}
                      delInv={delInv}
                      fmt={fmt}
                      totalGST={totalGST}
                      statusColor={statusColor}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
        <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300" data-testid="invoices-totals-row">
          <td className="p-4"></td>
          <td className="p-4 text-right uppercase tracking-[0.18em] text-xs text-slate-600">Grand Total</td>
          <td className="p-4 text-right">{totals.inv}</td>
          <td className="p-4 text-right serif-num">{fmt(totals.billed)}</td>
          <td className="p-4 text-right serif-num text-emerald-700">{fmt(totals.paid)}</td>
          <td className="p-4 text-right serif-num text-blue-700">{fmt(totals.tds)}</td>
          <td className="p-4 text-right serif-num text-red-700">{fmt(totals.balance)}</td>
          <td className="p-4 text-right text-xs text-amber-800">{totals.overdue > 0 ? `${totals.overdue} overdue` : "—"}</td>
        </tr>
      </tbody>
    </table>
  );
}

function ClientInvoicesDrill({ client, expandedInv, toggleInv, invPayments, user, canCreate, payForm,
                                setOpenPay, setPayForm, openEditInv, viewPdf, downloadPdf, sendReminder,
                                delInv, fmt, totalGST, statusColor }) {
  const byMonth = {}, byStatus = {};
  client.invoices.forEach((i) => {
    const m = (i.invoice_date || "").slice(0, 7) || "—";
    byMonth[m] = byMonth[m] || { billed: 0, paid: 0, balance: 0, count: 0 };
    byMonth[m].billed += i.total_amount || 0;
    byMonth[m].paid += i.paid_amount || 0;
    byMonth[m].balance += i.balance || 0;
    byMonth[m].count += 1;
    const st = i.status || "open";
    byStatus[st] = byStatus[st] || { count: 0, amount: 0 };
    byStatus[st].count += 1;
    byStatus[st].amount += i.total_amount || 0;
  });
  const monthRows = Object.entries(byMonth).sort((a, b) => a[0].localeCompare(b[0]));
  const statusRows = Object.entries(byStatus).sort((a, b) => b[1].amount - a[1].amount);

  return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="p-3 eyebrow border-b border-slate-100">Month-wise</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
              <tr>
                <th className="text-left p-2">Month</th>
                <th className="text-right p-2">#</th>
                <th className="text-right p-2">Billed</th>
                <th className="text-right p-2">Paid</th>
                <th className="text-right p-2">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {monthRows.map(([m, v]) => (
                <tr key={m}>
                  <td className="p-2 font-mono text-xs">{m}</td>
                  <td className="p-2 text-right">{v.count}</td>
                  <td className="p-2 text-right serif-num">{fmt(v.billed)}</td>
                  <td className="p-2 text-right serif-num text-emerald-700">{fmt(v.paid)}</td>
                  <td className="p-2 text-right serif-num text-red-700">{fmt(v.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="p-3 eyebrow border-b border-slate-100">Status-wise</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
              <tr>
                <th className="text-left p-2">Status</th>
                <th className="text-right p-2">Count</th>
                <th className="text-right p-2">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {statusRows.map(([s, v]) => (
                <tr key={s}>
                  <td className="p-2"><span className={`text-xs uppercase tracking-[0.2em] px-2 py-0.5 border rounded-full ${statusColor(s)}`}>{s}</span></td>
                  <td className="p-2 text-right">{v.count}</td>
                  <td className="p-2 text-right serif-num">{fmt(v.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <div className="p-3 eyebrow border-b border-slate-100">Invoice-wise · click a row to see line-items &amp; payment history</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
            <tr>
              <th className="p-2 w-8"></th>
              <th className="text-left p-2">No.</th>
              <th className="text-left p-2">Date</th>
              <th className="text-left p-2">Due</th>
              <th className="text-right p-2">Amount</th>
              <th className="text-right p-2">OPE</th>
              <th className="text-right p-2">GST</th>
              <th className="text-right p-2">Total</th>
              <th className="text-right p-2">Paid</th>
              <th className="text-right p-2">TDS</th>
              <th className="text-right p-2">Balance</th>
              <th className="text-left p-2">Status</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[...client.invoices].sort((a, b) => (b.invoice_date || "").localeCompare(a.invoice_date || "")).map((i) => {
              const isOpen = expandedInv.has(i.id);
              const payments = invPayments[i.id] || [];
              return (
                <Fragment key={i.id}>
                  <tr className="hover:bg-slate-50 cursor-pointer" data-testid={`inv-drill-row-${i.id}`}>
                    <td className="p-2" onClick={() => toggleInv(i.id)}>{isOpen ? <ChevronDown className="h-4 w-4 text-slate-500"/> : <ChevronRight className="h-4 w-4 text-slate-400"/>}</td>
                    <td className="p-2 font-mono text-xs" onClick={() => toggleInv(i.id)}>{i.invoice_number}</td>
                    <td className="p-2 font-mono text-xs" onClick={() => toggleInv(i.id)}>{i.invoice_date}</td>
                    <td className="p-2 font-mono text-xs" onClick={() => toggleInv(i.id)}>
                      {i.due_date}
                      {i.overdue && <div className="text-red-600 text-[10px] mt-0.5">{i.overdue_days}d overdue</div>}
                    </td>
                    <td className="p-2 text-right serif-num" onClick={() => toggleInv(i.id)}>{fmt(i.amount)}</td>
                    <td className="p-2 text-right serif-num text-slate-500" onClick={() => toggleInv(i.id)}>{fmt(i.out_of_pocket || 0)}</td>
                    <td className="p-2 text-right serif-num text-slate-500" onClick={() => toggleInv(i.id)}>{fmt(totalGST(i))}</td>
                    <td className="p-2 text-right serif-num font-semibold" onClick={() => toggleInv(i.id)}>{fmt(i.total_amount)}</td>
                    <td className="p-2 text-right serif-num text-emerald-700" onClick={() => toggleInv(i.id)}>{fmt(i.paid_amount)}</td>
                    <td className="p-2 text-right serif-num text-blue-700" onClick={() => toggleInv(i.id)}>{fmt(i.tds_amount || 0)}</td>
                    <td className="p-2 text-right serif-num text-red-700" onClick={() => toggleInv(i.id)}>{fmt(i.balance)}</td>
                    <td className="p-2" onClick={() => toggleInv(i.id)}><span className={`text-xs uppercase tracking-[0.2em] px-2 py-0.5 border rounded-full ${statusColor(i.status)}`}>{i.status}</span></td>
                    <td className="p-2 text-right whitespace-nowrap space-x-1">
                      {i.balance > 0 && ["partner","admin","accounts"].includes(user?.role) && <Button size="sm" onClick={(e) => { e.stopPropagation(); setOpenPay(i); setPayForm({ ...payForm, amount: i.balance }); }} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs" data-testid={`record-pay-${i.id}`}><IndianRupee className="h-3 w-3 mr-1"/>Pay</Button>}
                      {canCreate && <button onClick={(e) => { e.stopPropagation(); openEditInv(i); }} className="text-slate-500 hover:text-blue-700 inline-block" title="Edit invoice" data-testid={`inv-edit-${i.id}`}><Pencil className="h-4 w-4"/></button>}
                      <button onClick={(e) => { e.stopPropagation(); viewPdf(i); }} className="text-slate-500 hover:text-blue-700 inline-block" title="View PDF" data-testid={`inv-view-${i.id}`}><Eye className="h-4 w-4"/></button>
                      <button onClick={(e) => { e.stopPropagation(); downloadPdf(i); }} className="text-slate-500 hover:text-slate-900 inline-block" title="Download PDF" data-testid={`inv-pdf-${i.id}`}><FileDown className="h-4 w-4"/></button>
                      {i.overdue && canCreate && <button onClick={(e) => { e.stopPropagation(); sendReminder(i); }} className="text-amber-600 hover:text-amber-800 inline-block" title="Send reminder" data-testid={`inv-remind-${i.id}`}><Bell className="h-4 w-4"/></button>}
                      {canCreate && i.paid_amount === 0 && <button onClick={(e) => { e.stopPropagation(); delInv(i); }} className="text-slate-400 hover:text-red-600 inline-block" title="Delete"><Trash2 className="h-4 w-4"/></button>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-white">
                      <td colSpan={13} className="p-3">
                        <InvoiceLineItems invoice={i} payments={payments} fmt={fmt}/>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InvoiceLineItems({ invoice, payments, fmt }) {
  const gst = (invoice.cgst || 0) + (invoice.sgst || 0) + (invoice.igst || 0);
  const composition = [
    { label: "Professional Fees", amount: invoice.amount || 0 },
    invoice.out_of_pocket ? { label: "Out-of-Pocket (OPE)", amount: invoice.out_of_pocket, muted: true } : null,
    invoice.cgst ? { label: "CGST", amount: invoice.cgst, muted: true } : null,
    invoice.sgst ? { label: "SGST", amount: invoice.sgst, muted: true } : null,
    invoice.igst ? { label: "IGST", amount: invoice.igst, muted: true } : null,
  ].filter(Boolean);
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <div className="border border-slate-200 rounded-sm overflow-hidden bg-slate-50" data-testid={`inv-lineitems-${invoice.id}`}>
        <div className="p-2 eyebrow border-b border-slate-200 bg-white">Line items · Composition</div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-slate-100">
            {composition.map((c) => (
              <tr key={c.label} className="bg-white">
                <td className="p-2 text-slate-700">{c.label}</td>
                <td className={`p-2 text-right serif-num ${c.muted ? "text-slate-500" : ""}`}>{fmt(c.amount)}</td>
              </tr>
            ))}
            <tr className="bg-slate-50 font-semibold border-t border-slate-200">
              <td className="p-2 text-slate-700">Total Invoice Value</td>
              <td className="p-2 text-right serif-num">{fmt(invoice.total_amount || 0)}</td>
            </tr>
            {(gst > 0) && <tr className="bg-white text-xs text-slate-500"><td colSpan={2} className="px-2 pb-2">GST applied on Professional Fees {invoice.out_of_pocket ? "+ OPE" : ""}. Place of supply: {invoice.place_of_supply || "—"}. SAC: {invoice.sac_code || "9982"}.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="border border-slate-200 rounded-sm overflow-hidden bg-slate-50" data-testid={`inv-payments-${invoice.id}`}>
        <div className="p-2 eyebrow border-b border-slate-200 bg-white">Payment history</div>
        <table className="w-full text-sm">
          <thead className="bg-white text-slate-500 uppercase tracking-[0.18em] text-xs">
            <tr>
              <th className="text-left p-2">Date</th>
              <th className="text-left p-2">Mode</th>
              <th className="text-left p-2">Reference</th>
              <th className="text-right p-2">Amount</th>
              <th className="text-right p-2">TDS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {payments.length === 0 && <tr className="bg-white"><td colSpan={5} className="p-3 text-center text-slate-400 text-xs">No payments recorded yet.</td></tr>}
            {payments.map((p) => (
              <tr key={p.id} className="bg-white">
                <td className="p-2 font-mono text-xs">{p.payment_date}</td>
                <td className="p-2 text-xs">{p.mode}</td>
                <td className="p-2 text-xs font-mono">{p.reference || "—"}</td>
                <td className="p-2 text-right serif-num text-emerald-700">{fmt(p.amount)}</td>
                <td className="p-2 text-right serif-num text-blue-700">{fmt(p.tds_amount || 0)}</td>
              </tr>
            ))}
            {payments.length > 0 && (
              <tr className="bg-slate-50 font-semibold border-t border-slate-200">
                <td colSpan={3} className="p-2 uppercase tracking-[0.18em] text-xs text-slate-600">Sub-total</td>
                <td className="p-2 text-right serif-num text-emerald-700">{fmt(payments.reduce((s, p) => s + (p.amount || 0), 0))}</td>
                <td className="p-2 text-right serif-num text-blue-700">{fmt(payments.reduce((s, p) => s + (p.tds_amount || 0), 0))}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
